import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Swal from "sweetalert2";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import LogoutButton from "layouts/logout";

// Icons
import { Edit, Delete } from "@mui/icons-material";

// API
import api from "core/interceptors/api";

function StockUpdate() {
  const [loading, setLoading] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updateReason, setUpdateReason] = useState("");
  const navigate = useNavigate();
  const email = localStorage.getItem("email");



  const fetchStocks = async () => {
    setLoading(true);
    try {
      const res = await api.get("/product/fetch_all_products_no_email");
      setStocks(res.data.products || []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load stock data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  // Delete product
  const handleDelete = async (productId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will delete the product permanently.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        await api.delete(`/product/delete/${productId}`);
        Swal.fire("Deleted!", "The product has been deleted.", "success");
        fetchStocks();
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to delete product.", "error");
    }
  };

  const handleEdit = (stock) => {
    setSelectedStock({
      ...stock,
      stock_quantity: Number(stock.stock_quantity) || 0,
      added_quantity: 0, // 👈 New field for vendor input
    });
    setUpdateReason("");
    setOpenEditModal(true);
  };

  const handleUpdateStock = async () => {
    const addedQuantity = Number(selectedStock.added_quantity);
    const currentQuantity = Number(selectedStock.stock_quantity);

    if (isNaN(addedQuantity) || addedQuantity <= 0) {
      Swal.fire("Invalid Quantity", "Please enter a valid number greater than 0.", "warning");
      return;
    }

    if (!updateReason.trim()) {
      Swal.fire("Missing Reason", "Please provide a reason for stock update.", "info");
      return;
    }

    const newTotal = currentQuantity + addedQuantity;

    setLoading(true);
    try {
      const res = await api.post(`/product/update_stock/${selectedStock.product_id}`, {
        stock_quantity: newTotal, // ✅ total = current + newly added
        updated_by: email,
        update_reason: updateReason,
      });

      if (res.data.status) {
        Swal.fire("Success", `Stock increased by ${addedQuantity}. Total is now ${newTotal}.`, "success");
        fetchStocks();
        handleClose();
      } else {
        Swal.fire("Error", res.data.message || "Failed to update stock.", "error");
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      Swal.fire("Error", "Something went wrong while updating stock.", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setOpenEditModal(false);
    setSelectedStock({});
    setUpdateReason("");
  };

  // Update stock



  const columns = [
    { Header: "S.No.", accessor: "s_no" },
    { Header: "Product Name", accessor: "product_name" },
    { Header: "Current Stock", accessor: "stock_quantity" },
    { Header: "Restock Date", accessor: "restock_date" },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <IconButton color="primary" onClick={() => handleEdit(row.original)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(row.original.product_id)}>
            <Delete />
          </IconButton>
        </div>
      ),
    },
  ];

  const data = stocks.map((item, index) => ({
    s_no: index + 1,
    product_id: item.product_id,
    product_name: item.product_name,
    stock_quantity: item.stock_quantity || 0,
    restock_date: item.restock_date || "N/A",
  }));

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg">
                <MDTypography variant="h6" color="white">
                  Stock Management
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <DataTable
                  table={{ columns, rows: data }}
                  isSorted
                  entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                  showTotalEntries
                  canSearch
                  pagination={{
                    variant: "contained",
                    color: "dark",
                    page,
                    onPageChange: (_, newPage) => setPage(newPage),
                    rowsPerPage,
                    onRowsPerPageChange: (_, newRows) => setRowsPerPage(newRows),
                    count: stocks.length,
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Stock Modal */}
      <Dialog open={openEditModal} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Restock Product</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Product Name"
            value={selectedStock.product_name || ""}
            disabled
            margin="normal"
          />

          <TextField
            fullWidth
            label="Current Stock"
            value={selectedStock.stock_quantity || 0}
            disabled
            margin="normal"
          />

          <TextField
            fullWidth
            label="Add Quantity"
            type="number"
            value={selectedStock.added_quantity || ""}
            onChange={(e) =>
              setSelectedStock({ ...selectedStock, added_quantity: e.target.value })
            }
            margin="normal"
            helperText="Enter how many new items are added to stock"
          />

          <TextField
            fullWidth
            label="Summary"
            required
            multiline
            rows={2}
            value={updateReason}
            onChange={(e) => setUpdateReason(e.target.value)}
            margin="normal"
            error={!updateReason.trim() && updateReason.length > 0}
            helperText={
              !updateReason.trim() && updateReason.length > 0
                ? "Summary cannot be empty or just spaces."
                : "Explain why you're updating the stock (e.g., New shipment received)"
            }
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <MDButton color="info" onClick={handleUpdateStock} disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "Update Stock"}
          </MDButton>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default StockUpdate;
